
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SKIP_LOADING } from '../access/interceptor.service';
import {
  ListNotificationParams,
  Notification,
  NotificationFilters,
  NotificationListResponse,
  NotificationSeverity,
  NotificationType
} from '../models/notification';
import { PollingService } from './polling.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private readonly apiUrl = `${environment.url}/management/notification`;

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private destroy$ = new Subject<void>();

  // Guardamos las subs de pollings que iniciamos desde este servicio
  private pollSubs = new Map<string, Subscription>();

  private pollingKeys = {
    unreadCount: 'notification-unread-count',
    list: 'notification-list',
    detail: (id: string) => `notification-detail-${id}`
  };

  constructor(
    private http: HttpClient,
    private pollingService: PollingService
  ) { }

  ngOnDestroy(): void {
    this.stopAllPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getNotifications(params: ListNotificationParams): Observable<NotificationListResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page?.toString() || '0')
      .set('size', params.size?.toString() || '10')
      .set('storeId', params.storeId)
      .set('userId', params.userId);

    if (params.filters) {
      Object.keys(params.filters).forEach(key => {
        const value = (params.filters as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(`filters[${key}]`, value as any);
        } else if (key === 'correlationId' && value === null) {
          httpParams = httpParams.set('filters[correlationId]', '');
        }
      });
    }

    return this.http.get<NotificationListResponse>(
      `${this.apiUrl}`,
      { params: httpParams }
    ).pipe(
      tap(response => this.notificationsSubject.next(response.items)),
      catchError(error => {
        console.error('Error fetching notifications:', error);
        return throwError(() => error);
      })
    );
  }

  // Versión silenciosa (sin loader) para usar en polling
  private getNotificationsSilent(params: ListNotificationParams): Observable<NotificationListResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page?.toString() || '0')
      .set('size', params.size?.toString() || '10')
      .set('storeId', params.storeId)
      .set('userId', params.userId);

    if (params.filters) {
      Object.keys(params.filters).forEach(key => {
        const value = (params.filters as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(`filters[${key}]`, value as any);
        } else if (key === 'correlationId' && value === null) {
          httpParams = httpParams.set('filters[correlationId]', '');
        }
      });
    }

    const context = new HttpContext().set(SKIP_LOADING, true);

    return this.http.get<NotificationListResponse>(
      `${this.apiUrl}`,
      { params: httpParams, context }
    ).pipe(
      tap(response => {
        this.notificationsSubject.next(response.items);
      }),
      catchError(error => {
        console.error('Error fetching notifications:', error);
        return throwError(() => error);
      })
    );
  }

  getNotification(id: string): Observable<Notification> {
    return this.http.get<Notification>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Error fetching notification:', error);
        return throwError(() => error);
      })
    );
  }

  getChildNotifications(storeId: string, userId: string, parentId: string): Observable<Notification[]> {
    return this.http.get<Notification[]>(
      `${this.apiUrl}/${storeId}/${userId}/${parentId}/children`
    ).pipe(
      catchError(error => {
        console.error('Error fetching child notifications:', error);
        return throwError(() => error);
      })
    );
  }

  getUnreadCount(storeId: string, userId: string): Observable<number> {
    const context = new HttpContext().set(SKIP_LOADING, true);
    const timestamp = new Date().getTime();
    return this.http.get<number>(
      `${this.apiUrl}/${storeId}/${userId}/unread-count`,
      { context, params: { timestamp } }
    ).pipe(
      tap(count => {
        this.unreadCountSubject.next(count);
      }),
      catchError(error => {
        console.error('Error fetching unread count:', error);
        return throwError(() => error);
      })
    );
  }

  markAsRead(storeId: string, userId: string, notificationId: string): Observable<void> {
    return this.http.patch<void>(
      `${this.apiUrl}/${storeId}/${userId}/${notificationId}/mark-read`,
      {}
    ).pipe(
      tap(() => {
        const currentCount = this.unreadCountSubject.getValue();
        this.unreadCountSubject.next(Math.max(0, currentCount - 1));
        this.updateNotificationInList(notificationId, { isRead: true });
      }),
      catchError(error => {
        console.error('Error marking notification as read:', error);
        return throwError(() => error);
      })
    );
  }

  markMultipleAsRead(storeId: string, userId: string, notificationIds: string[]): Observable<void> {
    return this.http.patch<void>(
      `${this.apiUrl}/${storeId}/${userId}/mark-read-bulk`,
      notificationIds
    ).pipe(
      tap(() => {
        const currentCount = this.unreadCountSubject.getValue();
        this.unreadCountSubject.next(Math.max(0, currentCount - notificationIds.length));
        notificationIds.forEach(id => this.updateNotificationInList(id, { isRead: true }));
      }),
      catchError(error => {
        console.error('Error marking multiple notifications as read:', error);
        return throwError(() => error);
      })
    );
  }

  markAllAsRead(storeId: string, userId: string, filters?: NotificationFilters): Observable<void> {
    const body = filters || {};

    return this.http.patch<void>(
      `${this.apiUrl}/${storeId}/${userId}/mark-all-read`,
      body
    ).pipe(
      tap(() => {
        if (!filters || Object.keys(filters).length === 0) {
          this.unreadCountSubject.next(0);
          const notifications = this.notificationsSubject.getValue();
          const updated = notifications.map(n => ({ ...n, isRead: true }));
          this.notificationsSubject.next(updated);
        } else {
          this.refreshUnreadCount(storeId, userId);
        }
      }),
      catchError(error => {
        console.error('Error marking all notifications as read:', error);
        return throwError(() => error);
      })
    );
  }

  deleteNotification(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        const notifications = this.notificationsSubject.getValue();
        const deletedNotification = notifications.find(n => n.id === id);
        const filtered = notifications.filter(n => n.id !== id);
        this.notificationsSubject.next(filtered);

        if (deletedNotification && !deletedNotification.isRead) {
          const currentCount = this.unreadCountSubject.getValue();
          this.unreadCountSubject.next(Math.max(0, currentCount - 1));
        }
      }),
      catchError(error => {
        console.error('Error deleting notification:', error);
        return throwError(() => error);
      })
    );
  }

  // ---------- POLLING CONTROLLED LOCALLY ----------

  startPollingUnreadCount(
    storeId: string,
    userId: string,
    intervalMs: number = 3000
  ): void {
    const key = this.pollingKeys.unreadCount;
    this.stopPollingUnreadCount(); // por si ya había uno activo

    const source$ = this.getUnreadCount(storeId, userId); // ya usa SKIP_LOADING

    // startPolling devuelve Subscription y acepta callback opcional
    const sub = this.pollingService.startPolling(
      key,
      source$,
      { interval: intervalMs, retryAttempts: 3 },
      // callback opcional; en este caso el side-effect ya lo hace getUnreadCount -> tap
      () => { }
    );

    this.pollSubs.set(key, sub);
  }

  startPollingNotifications(
    params: ListNotificationParams,
    intervalMs: number = 3000
  ): void {
    const key = this.pollingKeys.list;
    this.stopPollingNotifications();

    const source$ = this.getNotificationsSilent(params); // silencioso

    const sub = this.pollingService.startPolling(
      key,
      source$,
      { interval: intervalMs, retryAttempts: 3 },
      // ya se hace tap en getNotificationsSilent, así que vacío
      () => { }
    );

    this.pollSubs.set(key, sub);
  }

  // Este devuelve un Observable para que un componente lo consuma.
  // Ese componente debe desuscribirse o llamar a stopPollingNotification(id).
  startPollingNotification(
    notificationId: string,
    intervalMs: number = 3000,
    next?: (n: Notification) => void
  ): Subscription {
    const key = this.pollingKeys.detail(notificationId);
    // si ya existía, lo cortamos
    this.stopPollingNotification(notificationId);

    const source$ = this.getNotification(notificationId);

    const sub = this.pollingService.startPolling(
      key,
      source$,
      { interval: intervalMs, retryAttempts: 3, stopCondition: () => false },
      // si no te pasan callback, no hacemos nada extra
      (value) => next?.(value)
    );

    // si querés poder cortarlo desde el NotificationService:
    this.pollSubs.set(key, sub);
    return sub;
  }

  stopPollingNotification(notificationId: string): void {
    const key = this.pollingKeys.detail(notificationId);
    this.pollSubs.get(key)?.unsubscribe();
    this.pollSubs.delete(key);
    this.pollingService.stopPolling(key);
  }

  stopPollingUnreadCount(): void {
    const key = this.pollingKeys.unreadCount;
    this.pollSubs.get(key)?.unsubscribe();
    this.pollSubs.delete(key);
    this.pollingService.stopPolling(key);
  }

  stopPollingNotifications(): void {
    const key = this.pollingKeys.list;
    this.pollSubs.get(key)?.unsubscribe();
    this.pollSubs.delete(key);
    this.pollingService.stopPolling(key);
  }

  stopAllPolling(): void {
    this.pollSubs.forEach(sub => sub.unsubscribe());
    this.pollSubs.clear();
    this.pollingService.stopAllPolling();
  }

  refreshUnreadCount(storeId: string, userId: string): void {
    this.getUnreadCount(storeId, userId).subscribe();
  }

  refreshNotifications(params: ListNotificationParams): void {
    this.getNotifications(params).subscribe();
  }

  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.getValue();
  }

  getCurrentNotifications(): Notification[] {
    return this.notificationsSubject.getValue();
  }

  resetUnreadCount(): void {
    this.unreadCountSubject.next(0);
  }

  incrementUnreadCount(): void {
    const currentCount = this.unreadCountSubject.getValue();
    this.unreadCountSubject.next(currentCount + 1);
  }

  setUnreadCount(count: number): void {
    this.unreadCountSubject.next(Math.max(0, count));
  }

  // test button on list notification.html
  simulateProgressNotification(storeId: string, userId: string): void {
    const fakeNotification: Notification = {
      id: `fake-progress-${Date.now()}`,
      userId: userId,
      storeId: storeId,
      type: NotificationType.INFO,
      title: 'Carga de productos',
      body: 'En proceso',
      severity: NotificationSeverity.MEDIUM,
      meta: {
        bulkInfo: {
          total: 50,
          successful: 0,
          failed: 0,
          entityType: 'productos'
        },
        errorCount: 0
      },
      isRead: false,
      readAt: null,
      filename: 'example.xlsx',
      deliveredAt: new Date(),
      deliveredChannel: 'backoffice',
      correlationId: null,
      source: 'products/batch',
      createdAt: new Date(),
      updatedAt: new Date(),
      deletedAt: null,
      expiresAt: null
    };

    const current = this.notificationsSubject.getValue();
    this.notificationsSubject.next([fakeNotification, ...current]);

    let progress = 0;
    let errors = 0;
    const interval = setInterval(() => {
      progress += Math.floor(Math.random() * 10) + 5;

      if (Math.random() < 0.1 && errors < 10) {
        errors++;
      }

      if (progress >= 50) {
        progress = 50;
        clearInterval(interval);

        fakeNotification.type = errors > 0 ? NotificationType.WARNING : NotificationType.SUCCESS;
        fakeNotification.body = errors > 0 ?
          `Finalizado con advertencia. ${50 - errors} de 50 productos procesados.` :
          'Finalizado con éxito. 50 productos procesados.';
        fakeNotification.meta = {
          ...fakeNotification.meta,
          bulkInfo: {
            total: 50,
            successful: 50 - errors,
            failed: errors,
            entityType: 'productos'
          },
          errorCount: errors
        };
      } else {
        fakeNotification.meta = {
          ...fakeNotification.meta,
          bulkInfo: {
            total: 50,
            successful: progress - errors,
            failed: errors,
            entityType: 'productos'
          },
          errorCount: errors
        };
      }

      const updated = this.notificationsSubject.getValue().map(n =>
        n.id === fakeNotification.id ? { ...fakeNotification } : n
      );
      this.notificationsSubject.next(updated);
    }, 2000);
  }

  private updateNotificationInList(id: string, updates: Partial<Notification>): void {
    const notifications = this.notificationsSubject.getValue();
    const updated = notifications.map(n =>
      n.id === id ? { ...n, ...updates } : n
    );
    this.notificationsSubject.next(updated);
  }

  clearNotificationsCache(): void {
    this.notificationsSubject.next([]);
  }
}
